import {
  ControlPoint,
  QualityPhoto,
  QualityReport,
  QualityStats,
} from '../types/quality'

/** Every control point across all sections, flattened in display order. */
export function allPoints(report: QualityReport): ControlPoint[] {
  return report.sections.flatMap((section) => section.points)
}

/** Photos attached to a given control point. */
export function photosForPoint(
  report: QualityReport,
  pointId: string,
): QualityPhoto[] {
  return report.photos.filter((photo) => photo.controlPointId === pointId)
}

/**
 * A point counts as missing photo only when the report demands documentation.
 * Photos are optional by default, so a registered point is done whether or
 * not anyone had a camera out.
 */
export function isMissingPhoto(
  report: QualityReport,
  point: ControlPoint,
): boolean {
  if (!report.requirePhotos) return false
  return point.requiresPhoto && photosForPoint(report, point.id).length === 0
}

/** A point is only complete when registered as OK and documented. */
export function isComplete(
  report: QualityReport,
  point: ControlPoint,
): boolean {
  return point.status === 'ok' && !isMissingPhoto(report, point)
}

export function getStats(report: QualityReport): QualityStats {
  const points = allPoints(report)
  const completedPoints = points.filter((p) => isComplete(report, p)).length
  const documentedPoints = points.filter(
    (p) => photosForPoint(report, p.id).length > 0,
  ).length
  return {
    totalPoints: points.length,
    completedPoints,
    deviations: points.filter((p) => p.status === 'deviation').length,
    missingPhoto: points.filter((p) => isMissingPhoto(report, p)).length,
    documentedPoints,
    unassignedPhotos: report.photos.filter((p) => !p.controlPointId).length,
    percent:
      points.length === 0
        ? 0
        : Math.round((completedPoints / points.length) * 100),
  }
}

/** Short point reference used on photo captions, e.g. "KP3". */
export function pointReference(
  report: QualityReport,
  pointId: string,
): string | null {
  const index = allPoints(report).findIndex((p) => p.id === pointId)
  return index === -1 ? null : `KP${index + 1}`
}

export function formatPhotoTimestamp(iso: string): string {
  const date = new Date(iso)
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${day}/${month} ${hours}:${minutes}`
}
